import React from "react";
import { Coins, Calculator, BarChart3, FileDown } from "lucide-react";
import ChapterShell from "@/tutorial/ChapterShell";
import { Section } from "@/tutorial/components/TutorialComponents";

/**
 * Chapter 11 — DEBT-VITA™ : Analyser la Dette en Vies
 *
 * Shell uniquement (T-DV5 cycle d'intégration).
 * Contenu narratif détaillé à compléter dans un cycle ultérieur (T-DV6).
 */
export default function Chapter11() {
  return (
    <ChapterShell id={11}>
      <Section
        id="section-11-1"
        icon={Coins}
        title="Pourquoi un module dédié à la dette ?"
      >
        <p>
          La dette publique n'est pas une promesse abstraite. C'est un{" "}
          <strong>prélèvement différé</strong> sur le temps de vie de la
          génération qui la contracte et de celles qui hériteront de son
          service. DEBT-VITA™ étend la théorie vitaliste à cet axe
          intergénérationnel pour rendre tangible un concept difficile à
          saisir : « 5 milliards de dette = 17 mois de vie de chaque actif,
          hypothéqués sur 30 ans ».
        </p>
      </Section>

      <Section
        id="section-11-2"
        icon={Calculator}
        title="Les 7 formules vitalistes de la dette (D1–D7)"
      >
        <p>
          DEBT-VITA™ ajoute sept indicateurs qui complètent les quatre
          formules canoniques de VITAE-PUBLICA™ :
        </p>
        <ul style={{ listStyle: "disc", paddingLeft: "1.25rem", margin: "8px 0" }}>
          <li>
            <strong>D1</strong> Dette par contribuable actif (UM/tête)
          </li>
          <li>
            <strong>D2</strong> Jours de vie hypothéqués
          </li>
          <li>
            <strong>D3</strong> Années de vie hypothéquées
          </li>
          <li>
            <strong>D4</strong> Pourcentage de carrière sacrifié à la dette
          </li>
          <li>
            <strong>D5</strong> Service annuel en vies humaines (intérêts +
            capital)
          </li>
          <li>
            <strong>D6</strong> Charge intergénérationnelle (projection N
            années)
          </li>
          <li>
            <strong>D7</strong> TAVD — Taux d'Aliénation Vitaliste de la Dette
          </li>
        </ul>
      </Section>

      <Section
        id="section-11-3"
        icon={BarChart3}
        title="Dashboard : 8 visualisations + 4 niveaux d'alerte"
      >
        <p>
          Le dashboard <em>Dette &amp; Vies</em> affiche une bannière d'alerte
          (vert/jaune/orange/rouge selon ratio AVH/carrière), six KPI cards
          et huit graphiques (thermomètre, donut créanciers, calendrier 365
          jours, projection 3 scénarios, charge intergénérationnelle, dette
          vs fiscalité, intérêts vs capital, treemap). Le sous-onglet{" "}
          <em>Comparer</em> permet de mettre côte à côte deux pays, deux
          années, ou un projet versus une référence FMI/OCDE saisie à la main.
        </p>
      </Section>

      <Section
        id="section-11-4"
        icon={FileDown}
        title="Rapport DEBT-VITA™ — PDF, Word, Excel"
      >
        <p>
          Trois formats sont disponibles depuis le bouton « Générer le rapport
          DEBT-VITA™ » :
        </p>
        <ul style={{ listStyle: "disc", paddingLeft: "1.25rem", margin: "8px 0" }}>
          <li>
            <strong>PDF</strong> 12+ pages thème sombre, couverture noire avec
            badge d'alerte coloré, méthodologie, 8 graphiques en HD,
            projection décennale, narrative LLM si générée, qualification
            juridique (Maastricht / FMI / Reinhart-Rogoff / TAVD).
          </li>
          <li>
            <strong>Excel</strong> 5 onglets dont l'onglet 2{" "}
            <em>Calculs Détaillés</em> contient de vraies formules Excel
            vivantes : modifiez B5, tout se recalcule.
          </li>
          <li>
            <strong>Word (.docx)</strong> document éditable structuré, idéal
            pour intégration dans un dossier de presse.
          </li>
        </ul>
        <p>
          Un toggle dédié sur l'étape 5 du wizard de rapport principal
          permet d'inclure une synthèse DEBT-VITA™ au rapport global du projet.
        </p>
      </Section>

      <Section id="section-11-5" title="Pour aller plus loin">
        <p>
          Ce chapitre est un aperçu. Le contenu pédagogique détaillé (vidéos,
          quiz, simulations interactives) sera ajouté dans le prochain cycle
          de mise à jour du tutoriel. En attendant, ouvrez le module{" "}
          <code>/app/projects/&lt;id&gt;/debt</code> pour explorer
          DEBT-VITA™ sur l'un de vos projets.
        </p>
      </Section>
    </ChapterShell>
  );
}
